import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  X,
  Home,
  Calendar,
  Users,
  BookOpen,
  UserCheck,
  Shield,
  Megaphone,
  CreditCard,
  BarChart3,
  QrCode,
  ScanLine,
  Settings,
  MessageCircle,
  LogOut,
  PieChart,
  ClipboardList,
  History,
  User,
  Banknote,
  Building2,
  Dumbbell
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose }) => {
  const { currentUser, logout } = useAuth();
  const { currentBusiness } = useBusiness();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Reserva', href: '/calendar', icon: Calendar },
    { name: 'Clases', href: '/classes', icon: BookOpen },
    { name: 'WOD', href: '/wod', icon: Dumbbell },
    { name: 'Instructores', href: '/instructors', icon: UserCheck },
    { name: 'Novedades', href: '/announcements', icon: Megaphone },
    { name: 'Membresías y Bonos', href: '/memberships', icon: CreditCard },
    { name: 'Mi Código', href: '/my-code', icon: QrCode },
    { name: 'Háblanos', href: '/chat', icon: MessageCircle },
    { name: 'Mi Perfil', href: '/profile', icon: Users },
  ];

  const adminNavigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: BarChart3 },
    { name: 'Estadísticas', href: '/admin/statistics', icon: PieChart },
    { name: 'Lector QR', href: '/admin/qr-reader', icon: ScanLine },
    { name: 'Usuarios', href: '/admin/users', icon: Users },
    { name: 'Instructores', href: '/admin/instructors', icon: UserCheck },
    { name: 'Clases', href: '/admin/class-types', icon: BookOpen },
    { name: 'Calendario', href: '/admin/calendar-management', icon: Calendar },
    { name: 'WODs', href: '/admin/wods', icon: Dumbbell },
    { name: 'Membresías', href: '/admin/class-passes', icon: CreditCard },
    { name: 'Cobros', href: '/admin/payments', icon: Banknote },
    { name: 'Anuncios', href: '/admin/announcements', icon: Megaphone },
    { name: 'Configuración', href: '/admin/config', icon: Settings },
  ];

  const superAdminNavigation = [
    { name: 'Visión Global', href: '/super/overview', icon: BarChart3 },
    { name: 'Negocios', href: '/super/businesses', icon: Building2 },
    { name: 'Usuarios', href: '/super/users', icon: Users },
    { name: 'Comunicaciones', href: '/super/communications', icon: Megaphone },
    { name: 'Configuración', href: '/super/config', icon: Settings },
    { name: 'Logs', href: '/super/logs', icon: ClipboardList },
    { name: 'Mantenimiento', href: '/super/maintenance', icon: Shield },
  ];

  const instructorNavigation = [
    { name: 'Mi Agenda', href: '/instructor', icon: Home },
    { name: 'Mis Clases', href: '/instructor/classes', icon: Calendar },
    { name: 'Pasar Lista', href: '/instructor/attendance', icon: ClipboardList },
    { name: 'Historial', href: '/instructor/history', icon: History },
    { name: 'Mi Perfil', href: '/instructor/profile', icon: User },
  ];

  const getNavLinkClass = (isActive: boolean) => {
    return `flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${isActive
      ? 'bg-primary-500/20 text-primary-400'
      : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
      }`;
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[18rem] bg-gray-950 transform transition-transform duration-300 ease-out lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-5 border-b border-gray-800">
            <div className="flex items-center">
              {currentBusiness?.logo ? (
                <img
                  src={currentBusiness.logo}
                  alt={currentBusiness.name}
                  className="w-10 h-10 rounded-xl object-cover mr-3"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center mr-3 shadow-lg shadow-primary-500/20">
                  <span className="text-white font-bold text-xl">
                    {currentBusiness?.name?.charAt(0) || 'D'}
                  </span>
                </div>
              )}
              <span className="text-white text-lg font-bold truncate max-w-[140px]">
                {currentBusiness?.name || 'Mi Negocio'}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* User info */}
          <div className="px-5 py-4 border-b border-gray-800">
            <div className="flex items-center">
              {currentUser?.profilePhotoUrl ? (
                <img
                  src={currentUser.profilePhotoUrl}
                  alt={`${currentUser.firstName} ${currentUser.lastName}`}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-700"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center ring-2 ring-gray-700">
                  <span className="text-white font-bold">
                    {currentUser?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
              <div className="ml-3">
                <p className="text-base font-medium text-white">
                  {currentUser?.firstName} {currentUser?.lastName}
                </p>
                <p className="text-sm text-gray-500 capitalize">
                  {currentUser?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <nav className="space-y-1">
              {/* Main Navigation - solo visible para usuarios normales */}
              {currentUser?.role === 'user' && (
                <div className="space-y-1">
                  {navigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) => getNavLinkClass(isActive)}
                      onClick={onClose}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Instructor Navigation - solo visible para instructores */}
              {currentUser?.role === 'instructor' && (
                <div className="space-y-1">
                  <div className="pt-2 pb-3">
                    <h3 className="px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Panel Instructor
                    </h3>
                  </div>
                  {instructorNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) => getNavLinkClass(isActive)}
                      onClick={onClose}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Admin Navigation - solo visible para admins */}
              {currentUser?.role === 'admin' && (
                <div className="space-y-1">
                  <div className="pt-2 pb-3">
                    <h3 className="px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Administración
                    </h3>
                  </div>
                  {adminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) => getNavLinkClass(isActive)}
                      onClick={onClose}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Super Admin Navigation - solo visible para super admins */}
              {currentUser?.role === 'super_admin' && (
                <div className="space-y-1">
                  <div className="pt-2 pb-3">
                    <h3 className="px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Super Admin
                    </h3>
                  </div>
                  {superAdminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) => getNavLinkClass(isActive)}
                      onClick={onClose}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </nav>
          </div>

          {/* Logout button */}
          <div className="flex-shrink-0 border-t border-gray-800 p-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-base font-medium text-red-400 hover:bg-gray-800 hover:text-red-300 rounded-xl transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileSidebar;
